/* Sign In/Out Form Component */
import React, { useEffect, useRef } from "react";
import { LOCATIONS, LOCATION_ICONS } from '../utils/constants';
import { useNavigate } from "react-router";
import "../css//SignInOutForm.css";
import { getMagniEmployees, recordEvent,checkEventExistsForUser, updateEvent, getSignInsForLocation  } from "../utils/api";
import { EmployeeSignIns, MagniEmployee, SignInTableData } from "./Shared/Types";
import { toast, ToastContainer } from "react-toastify";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { CircularProgress } from "@mui/material";
import moment from "moment";
import { off } from "process";

interface SignInOutFormProps {
  formType: string;
  currentLocation: string;
}

const SignInOutForm = ({ formType, currentLocation }:SignInOutFormProps) => {



  const navigate = useNavigate();
  const [name, setName] = React.useState<MagniEmployee>();
  const [submitting, setSubmitting] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [nameSuggestions, setNameSuggestions] = React.useState<any>([]);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = React.useState(-1);
  const [destination, setDestination] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [isFirstVisit, setIsFirstVisit] = React.useState(false);
  const [company, setCompany] = React.useState('');
  const [contactPerson, setContactPerson] = React.useState('');
  const [signedInData, setSignedInData] = React.useState<EmployeeSignIns[]>([]);
  const [isFormSubmitted, setIsFormSubmitted] = React.useState(false);

  const [isSignInForm, setIsSignInForm] = React.useState(formType.includes('sign-in') || formType.includes('main-office-sign-out'));
  
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsListRef = useRef<HTMLUListElement>(null);

  // Mock data for name suggestions - replace with actual data source
  const [magniNames, setMagniNames] = React.useState<MagniEmployee[]>([]);
  
  // Check if form is for visitors
  const isVisitorForm = formType.includes('visitor');
  
  // Filter suggestions based on search term
  const filterSuggestions = (searchText:string) => {
    if (!searchText || isVisitorForm) return [];
    const filtered = magniNames.filter((name:MagniEmployee) => 
      name.username.toLowerCase().includes(searchText.toLowerCase())
    );
    return filtered;
  };
  
  // Scroll selected item into view when navigating with keyboard
  useEffect(() => {
    if (selectedSuggestionIndex >= 0 && suggestionsListRef.current) {
      const selectedElement = suggestionsListRef.current.children[selectedSuggestionIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedSuggestionIndex]);
  
  // Handle input change for name field
  const handleNameInputChange = (e:any) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSelectedSuggestionIndex(-1);
    
    // Skip autocomplete for visitor forms
    if (value.trim() && !isVisitorForm) {
      const filteredSuggestions = filterSuggestions(value);
      setName(nameSuggestions.find((suggestion:MagniEmployee) => suggestion.username === value));
      setNameSuggestions(filteredSuggestions);
      setShowSuggestions(true);
    } else {
      setNameSuggestions([]);
      setShowSuggestions(false);
      let login = {username: value, location: 'n/a', officeLocation: 'n/a'};
      setName(login);
    }
  };
  
  const IsHomeOffice =(location:string, employee:MagniEmployee) => {
      switch (location) {
        case 'Birmingham':
          return employee.location === 'The Magni Group, Inc.' || employee.officeLocation === 'Birmingham';
        case 'Detroit':
          return employee.location.includes('Detroit') || employee.officeLocation === 'Hammond';
        case 'Kentucky':
          return employee.location.includes('Kentucky') || employee.officeLocation === 'Independence';
        case 'Troy':
          return employee.location.includes('Troy') || employee.officeLocation === 'Troy';
        case 'Shelby':
          return employee.location.includes('Shelby') || employee.officeLocation === 'Shelby';
        case 'Madison Heights': 
          return employee.location.includes('Madison Heights') || employee.officeLocation === 'Madison Heights';
        case 'Ferndale':
          return employee.location.includes('Ferndale') || employee.officeLocation === 'Ferndale';
        case 'Portland':
          return employee.location.includes('Portland') || employee.officeLocation === 'Portland';
        default:
          return false;

  }
}

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion:MagniEmployee) => {
    setName(suggestion);
    setSearchTerm(suggestion.username);
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
    inputRef.current?.focus();
  };
  
  // Handle keyboard navigation
  const handleKeyDown = (e:any) => {
    if (!showSuggestions || nameSuggestions.length === 0) return;
    
    // Arrow down
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => 
        prev < nameSuggestions.length - 1 ? prev + 1 : prev
      );
    }
    // Arrow up
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : 0);
    }
    // Enter key
    else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
      e.preventDefault();
      handleSelectSuggestion(nameSuggestions[selectedSuggestionIndex]);
    }
    // Escape key
    else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }
  };
  
  // Determine form title and subtitle based on form type
  let formTitle = '';
  let formSubtitle = '';
  let formFields = [];
  
  switch (formType) {
    case 'employee-sign-in':
      formTitle = 'Employee Sign In';
      formSubtitle = `Please enter your name to sign in to ${currentLocation}.`;
      formFields = ['name'];
      break;
    case 'employee-sign-out':
      formTitle = 'Employee Sign Out';
      formSubtitle = `Please enter your name to sign out from ${currentLocation}.`;
      formFields = ['name'];
      break;
    case 'visitor-sign-in':
      formTitle = 'Visitor Sign In';
      formSubtitle = `Please fill out the form to sign in to ${currentLocation}.`;
      formFields = ['name', 'company', 'contactPerson', 'isFirstVisit'];
      break;
    case 'visitor-sign-out':
      formTitle = 'Visitor Sign Out';
      formSubtitle = `Please enter your name to sign out from ${currentLocation}.`;
      formFields = ['name'];
      break;
    case 'main-office-sign-out':
      formTitle = `Sign Out of ${currentLocation}`;
      formSubtitle = `Please fill out the form to sign out from ${currentLocation}.`;
      formFields = ['name', 'destination', 'description'];
      break;
    case 'visiting-office-sign-in':
      formTitle = `Sign In to ${currentLocation}`;
      formSubtitle = `Please fill out the form to sign in to ${currentLocation}.`;
      formFields = ['name'];
      break;
    default:
      formTitle = 'Sign In/Out';
      formSubtitle = 'Please fill out the form.';
      formFields = ['name'];
  }
  
  const handleSubmit = async (e:any) => {
    e.preventDefault();
    setIsFormSubmitted(true);
    // Add additional fields based on form type1
    if (name === undefined ) {
      setIsFormSubmitted(false);
      return
    }
    const employeeInfo = magniNames.find((emp:MagniEmployee) => emp.username === name.username);
    const officeLocation = employeeInfo?.officeLocation !== null ? employeeInfo?.officeLocation : employeeInfo.location;


    const updateName = {
      ...name,
      location: officeLocation ?? currentLocation,
    }
    setName(updateName)

    
    const signEvent: SignInTableData = {
      SignInId: 0,
      UserName: name.username,
      eventDate:  moment().format("YYYY-MM-DDTHH:mm:ssZ") ,
      eventType: formType.includes('sign-in') ? 'in' : 'out',
      Location: formType.includes('main-office-sign-out') ? currentLocation + '->' + destination  : currentLocation,
      MagniEmployee: name.location !== 'n/a' ? true : false,
      IsMainOffice: formType.includes('visitor') ? false : IsHomeOffice(currentLocation, updateName),
      ReasonForVisit: description,
    }

    const eventExists = await checkEventExistsForUser(signEvent);

    if (eventExists.SignInId !==0 && formType.includes('sign-out') && signEvent.MagniEmployee) {
      signEvent.SignInId = eventExists.SignInId;
      await updateEvent(signEvent)
      .then((result:any) => {
        if (result === 'Failed to update event') {
          toast.error('Failed to update event');
        }
        else {
          setSubmitting(true);
          toast.success('Sign Out Updated');

          setTimeout(() => {
            navigate('/signout');
          }, 3000);

        }
        
      });
      setIsFormSubmitted(false);
      setSubmitting(false);
      return;

    } 
    else if (eventExists.SignInId !==0 && formType.includes('sign-in') && signEvent.MagniEmployee) {
      await updateEvent(signEvent)
      .then( async (result:any) => {
        if (result === 'Failed to update event') {
          toast.error('Failed to update event');
        }
        else {
          await recordEvent(signEvent)
          .then((result:any) => {
            if (result === 'Failed to record event') {
              toast.error('Failed to record event');
              setIsFormSubmitted(false);
              return;
            }
          setSubmitting(true);
          toast.success('Sign In Updated');

          setTimeout(() => {
            navigate('/signout');
          }, 3000);

        })
        
      }})
    }
    else
    {

       await recordEvent(signEvent)
      .then((result:any) => {

        if (result === 'Failed to record event') {
          toast.error('Failed to record event');
          setIsFormSubmitted(false);
          return;
        }
        // Handle successful submission (e.g., navigate to another page)
        const successMessage = formType.includes('sign-in') ? 'Sign In Successful' : 'Sign Out Successful';
        toast.success(successMessage);
        setTimeout(() => {
          navigate('/signout');
        }, 3000);
      })
      .catch((error) => {
        console.error("Error submitting form:", error);
        setIsFormSubmitted(false);
      });
    }
  };
  
  // Determine if form is valid based on required fields

  useEffect(() => {
    const fetchMagniEmployees = async () => {
      // Only fetch employee names if not a visitor form
      if (!isVisitorForm && isSignInForm) {
        try {
          const employees = await getMagniEmployees();
          setMagniNames(employees);
        } catch (error) {
          console.error("Error fetching employee names:", error);
        }
      }
      else if (formType.includes('sign-out')) {
        const signedInData = await getSignInsForLocation(currentLocation, formType.includes('employee'));
        setSignedInData(signedInData);
        const employees = await getMagniEmployees();
        setMagniNames(employees);
      }
    };
    
    fetchMagniEmployees();
   }, [isVisitorForm]);
  
  return (
    <div className="form-screen">
      <ToastContainer />
      <button className="back-button" onClick={() => navigate('/signout')}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Back
      </button>
      
      <h2 className="form-title">{formTitle}</h2>
      <p className="form-subtitle">{formSubtitle}</p>
      
      <form onSubmit={handleSubmit}>
        {formFields.includes('name') && (
            <div className="form-group">
            { signedInData.length > 0 && formType.includes('main-office-sign-out') === false ? <div > 
              <label className="form-label" htmlFor="nameSelector">Name</label>
              <select
              className="form-input"
              value={name?.username || ''}
              onChange={(e) => {
                const selectedName = signedInData.find((emp:EmployeeSignIns) => emp.name === e.target.value);
                let user:MagniEmployee = {username: selectedName?.name ?? '', location: currentLocation, officeLocation: ''};
                setName(user);
                setSelectedSuggestionIndex(-1);
              }}
              id="nameSelector"
              required
              >
              <option value="">Select your name</option>
              {signedInData.map((emp:EmployeeSignIns) => (
                <option key={emp.name} value={emp.name}>{emp.name}</option>
              ))}
              </select>
            </div> :
            <div>
            <label className="form-label" htmlFor="name">Name</label>
            <div className="autocomplete-container">
              <input 
              ref={inputRef}
              id="name"
              className="form-input" 
              type="text" 
              value={searchTerm}
              onChange={handleNameInputChange}
              onKeyDown={isVisitorForm ? undefined : handleKeyDown}
              autoComplete="off"
              placeholder="Enter your full name"
              required
              onFocus={() => !isVisitorForm && searchTerm && setShowSuggestions(true)}
              onBlur={() => !isVisitorForm && setTimeout(() => setShowSuggestions(false), 200)}
              />
              {!isVisitorForm && showSuggestions && nameSuggestions.length > 0 && isSignInForm && (
              <ul className="suggestions-list" ref={suggestionsListRef}>
                {nameSuggestions.map((suggestion:MagniEmployee, index:any) => (
                <li 
                  key={index} 
                  onClick={() => handleSelectSuggestion(suggestion)}
                  className={`suggestion-item ${index === selectedSuggestionIndex ? 'selected' : ''}`}
                  onMouseEnter={() => setSelectedSuggestionIndex(index)}
                >
                  {suggestion.username}
                </li>
                ))}
              </ul>
              )}
            </div>
            </div>}
            </div>
        )}
        
        {formFields.includes('company') && (
          <div className="form-group">
            <label className="form-label" htmlFor="company">Company</label>
            <input 
              id="company"
              className="form-input" 
              type="text" 
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Enter your company name"
              required
            />
          </div>
        )}
        
        {formFields.includes('contactPerson') && (
          <div className="form-group">
            <label className="form-label" htmlFor="contact">Who are you here to see?</label>
            <input 
              id="contact"
              className="form-input" 
              type="text" 
              value={contactPerson}
              onChange={(e) => setContactPerson(e.target.value)}
              placeholder="Enter the name of your contact person"
              required
            />
          </div>
        )}
        
        {formFields.includes('destination') && (
          <div className="form-group">
            <label className="form-label" htmlFor="destination">Destination</label>
            <select
              id="destination"
              className="form-select"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              required
            >
              <option value="">Select destination</option>
              {LOCATIONS.map(loc => (
                <option key={loc} value={loc}>{LOCATION_ICONS[loc]} {loc}</option>
              ))}
            </select>
          </div>
        )}
        
        {formFields.includes('description') && (
          <div className="form-group">
            <label className="form-label" htmlFor="description">Description (Optional)</label>
            <input 
              id="description"
              className="form-input" 
              type="text" 
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of your visit"
            />
          </div>
        )}
        <div className="form-group" style={{ display: 'flex', justifyContent: 'center' }}>
          <Box sx={{display:'flex', alignItems:'center',}}>
            <Box sx={{m:1, position:'relative',}}>
              <Button
                variant="contained"
                disabled={isFormSubmitted}
                onClick={handleSubmit}>
                
                  {formType.includes('sign-in') ? 'Sign In' : 'Sign Out'}
                </Button>
                {
                  submitting && (
                    <CircularProgress
                    size={24}
                    sx={{
                      color: 'secondary',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: '-12px',
                      marginLeft: '-12px',
                    }}
                    />  
                  )
                }
            </Box>
          </Box>
        </div>
        
      </form>
    </div>
  );
};

export default SignInOutForm;